// frontend/src/App.js
import FuturesChartUser from './components/charts/FuturesChartUser';
import { BrowserRouter as Router, Routes, Route, Link, Navigate } from 'react-router-dom';
import CalendarPage from './components/pages/CalendarPage';
import TopVolumeTablesByInterval from './components/tables/TopVolumeTablesByInterval';
import useSyncedLocalStorage from './hooks/useSyncedLocalStorage';
import BoxLineInputPage from './components/pages/BoxLineInputPage';
import FuturesChartAdmin from './components/charts/FuturesChartAdmin';
import FuturesChartSimulation from './components/charts/FuturesChartSimulation';
import MemoInputPage from './components/pages/MemoInputPage';
import LoginPage from './components/pages/LoginPage';
import SignupPage from './components/pages/SignupPage';
import Navbar from './components/layout/Navbar';
import RequireAdmin from './components/auth/RequireAdmin';
import RequireAuth from './components/auth/RequireAuth';
import RequireDeveloper from './components/auth/RequireDeveloper';
import AdminManualBoxLinesPage from './components/pages/AdminManualBoxLinesPage';
import AutoTradingDetailHistoryPage from './components/pages/AutoTradingDetailHistoryPage';
import LsOverseasAccountPage from './components/pages/LsOverseasAccountPage';
import LsOverseasAccountPageDev from './components/pages/LsOverseasAccountPageDev';

export default function App() {
  const [visiblePlotLines, setVisiblePlotLines] = useSyncedLocalStorage('visiblePlotLines', {});

  // onToggleLine은 두번째 인자가 있으면 해당 값으로, 없으면 토글 처리
  // meta가 넘어오는 경우에는 { checked, ...meta } 형태로 저장
  // 기존에 object가 저장된 경우에는 그 안의 checked만 업데이트
  const onToggleLine = (toggleKey, newValue, meta) => {
    setVisiblePlotLines((prev) => {
      const prevVal = prev[toggleKey];

      // meta가 주어진 경우: { checked, ...meta } 형태로 저장
      if (meta) {
        const prevChecked =
          typeof prevVal === 'object' && prevVal !== null ? !!prevVal.checked : !!prevVal;
        const checked = newValue !== undefined ? newValue : !prevChecked;

        return {
          ...prev,
          [toggleKey]: {
            ...meta,
            checked,
          },
        };
      }

      // meta 없이 호출된 경우: 기존 구조 유지
      if (typeof prevVal === 'object' && prevVal !== null) {
        const checked = newValue !== undefined ? newValue : !prevVal.checked;

        return {
          ...prev,
          [toggleKey]: {
            ...prevVal,
            checked,
          },
        };
      }

      return {
        ...prev,
        [toggleKey]: newValue !== undefined ? newValue : !prevVal,
      };
    });
  };

  return (
    <Router>
      <Navbar />
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />

        {/* Developer-only routes */}
        <Route
          path="/developer/*"
          element={
            <RequireDeveloper>
              <Routes>
                <Route
                  path="sim-chart"
                  element={
                    <FuturesChartSimulation
                      visiblePlotLines={visiblePlotLines}
                      onToggleLine={onToggleLine}
                    />
                  }
                />
                <Route
                  path="admin-chart"
                  element={
                    <FuturesChartAdmin
                      visiblePlotLines={visiblePlotLines}
                      onToggleLine={onToggleLine}
                    />
                  }
                />
                <Route
                  path="top-volume-tables"
                  element={
                    <TopVolumeTablesByInterval
                      visiblePlotLines={visiblePlotLines}
                      onToggleLine={onToggleLine}
                    />
                  }
                />
                <Route
                  path="calendar-input"
                  element={
                    <>
                      <CalendarPage />
                    </>
                  }
                />
                <Route
                  path="boxline-input"
                  element={
                    <BoxLineInputPage
                      visiblePlotLines={visiblePlotLines}
                      onToggleLine={onToggleLine}
                    />
                  }
                />
                <Route path="memo-input" element={<MemoInputPage />} />
                <Route path="manage-manual-boxlines" element={<AdminManualBoxLinesPage />} />

                <Route path="detail-history" element={<AutoTradingDetailHistoryPage />} />
                <Route path="ls-overseas-account" element={<LsOverseasAccountPageDev />} />
              </Routes>
            </RequireDeveloper>
          }
        />

        {/* Admin-only routes */}
        <Route
          path="/admin/*"
          element={
            <RequireAdmin>
              <Routes>
                <Route
                  path="admin-chart"
                  element={
                    <FuturesChartAdmin
                      visiblePlotLines={visiblePlotLines}
                      onToggleLine={onToggleLine}
                    />
                  }
                />
                <Route
                  path="sim-chart"
                  element={
                    <FuturesChartSimulation
                      visiblePlotLines={visiblePlotLines}
                      onToggleLine={onToggleLine}
                    />
                  }
                />
                <Route
                  path="top-volume-tables"
                  element={
                    <TopVolumeTablesByInterval
                      visiblePlotLines={visiblePlotLines}
                      onToggleLine={onToggleLine}
                    />
                  }
                />
                <Route
                  path="calendar-input"
                  element={
                    <>
                      <CalendarPage />
                    </>
                  }
                />
                <Route
                  path="boxline-input"
                  element={
                    <BoxLineInputPage
                      visiblePlotLines={visiblePlotLines}
                      onToggleLine={onToggleLine}
                    />
                  }
                />
                <Route path="memo-input" element={<MemoInputPage />} />
                <Route path="manage-manual-boxlines" element={<AdminManualBoxLinesPage />} />

                <Route path="detail-history" element={<AutoTradingDetailHistoryPage />} />
                <Route path="ls-overseas-account" element={<LsOverseasAccountPage />} />

                {/* Fallback within admin */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </RequireAdmin>
          }
        />

        {/* Authenticated user routes */}
        <Route
          path="/"
          element={
            <RequireAuth allowedForNonAdmin={['/']}>
              <FuturesChartUser visiblePlotLines={visiblePlotLines} onToggleLine={onToggleLine} />
            </RequireAuth>
          }
        />
        <Route
          path="/ls-overseas-account"
          element={
            <RequireAuth allowedForNonAdmin={['/ls-overseas-account']}>
              <LsOverseasAccountPage />
            </RequireAuth>
          }
        />

        {/* Global fallback */}
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </Router>
  );
}
